/**
 * Aplica filtro salvo de negócios no query builder do Supabase — mesma lógica do Kanban (`useDealsByStage`).
 * Mantém lista (/deals/list) e kanban alinhados (custom_data JSONB, datas, status, etc.).
 */
import {
  startOfDay, endOfDay, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, startOfYear, endOfYear,
  startOfQuarter, endOfQuarter,
  subDays, subWeeks, subMonths, subYears, subQuarters,
  parseISO, isValid,
} from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { filterHasFieldRule } from "@/lib/filterEngine";
import { isCadenceFilterField, normalizeFilterFieldPath } from "@/lib/relationalFieldRegistry";
import { queryClient, queryConfigs } from "@/lib/queryClient";
import { splitFilterValue, isMultiToken, postgrestInList } from "@/lib/filters/valueTokens";

const DEAL_COLS = new Set([
  "status", "title", "amount", "stage_id", "pipeline_id",
  "probability", "close_date", "won_at", "lost_at", "owner_id", "lead_id",
  "company_id", "created_at", "updated_at", "source",
  "loss_reason_id", "custom_loss_reason", "description", "sdr_responsavel",
]);

const NUMERIC_COLS = new Set(["amount", "probability"]);

const DATE_COLS = new Set(["created_at", "updated_at", "close_date", "won_at", "lost_at"]);

const RELATIVE_DATE_OPS = new Set([
  "today", "yesterday",
  "this_week", "last_week",
  "this_month", "last_month",
  "this_quarter", "last_quarter",
  "this_year", "last_year",
]);

const SERVER_OPS: Record<string, true> = {
  equals: true, not_equals: true, contains: true, not_contains: true,
  greater_than: true, less_than: true, is_empty: true, is_not_empty: true,
  after: true, before: true, gte: true, lte: true,
};

function relativeDateToRange(operator: string): { from: string; to: string } | null {
  const now = new Date();
  let from: Date;
  let to: Date;
  switch (operator) {
    case "today":
      from = startOfDay(now); to = endOfDay(now); break;
    case "yesterday": {
      const yd = subDays(now, 1);
      from = startOfDay(yd); to = endOfDay(yd); break;
    }
    case "this_week":
      from = startOfWeek(now, { weekStartsOn: 1 });
      to = endOfWeek(now, { weekStartsOn: 1 }); break;
    case "last_week": {
      const prev = subWeeks(now, 1);
      from = startOfWeek(prev, { weekStartsOn: 1 });
      to = endOfWeek(prev, { weekStartsOn: 1 }); break;
    }
    case "this_month":
      from = startOfMonth(now); to = endOfMonth(now); break;
    case "last_month": {
      const prev = subMonths(now, 1);
      from = startOfMonth(prev); to = endOfMonth(prev); break;
    }
    case "this_quarter":
      from = startOfQuarter(now); to = endOfQuarter(now); break;
    case "last_quarter": {
      const prev = subQuarters(now, 1);
      from = startOfQuarter(prev); to = endOfQuarter(prev); break;
    }
    case "this_year":
      from = startOfYear(now); to = endOfYear(now); break;
    case "last_year": {
      const prev = subYears(now, 1);
      from = startOfYear(prev); to = endOfYear(prev); break;
    }
    default: return null;
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function parseCsvTokens(raw: string): string[] {
  return splitFilterValue(raw);
}

/**
 * equals / not_equals com vários valores — alinhado ao filterEngine e ao ValueInput multiselect.
 *
 * O gate era `raw.includes(",")`, que ignorava o formato de unit separator e ainda
 * tratava uma ÚNICA opção com vírgula ("Sim, auxílio doença") como multi-valor.
 * Ver `valueTokens.ts`.
 */
function applyMultiEqualityOnDealColumn(
  q: any,
  campo: string,
  operador: "equals" | "not_equals",
  raw: string
): any | null {
  if (!isMultiToken(raw)) return null;
  const parts = parseCsvTokens(raw);

  if (operador === "equals") {
    if (campo === "owner_id") {
      const hasNoOwner = parts.includes("__no_owner__");
      const ids = parts.filter((p) => p !== "__no_owner__");
      if (hasNoOwner && ids.length === 0) return q.is("owner_id", null);
      if (hasNoOwner) return q.or(`owner_id.is.null,owner_id.in.(${ids.join(",")})`);
      return q.in("owner_id", ids);
    }
    return q.in(campo, parts);
  }

  if (parts.includes("__no_owner__")) return null;

  // Citação obrigatória: token com vírgula/parêntese quebraria a lista do PostgREST.
  return q.not(campo, "in", `(${postgrestInList(parts)})`);
}

/**
 * Valor pronto para um `eq`/`neq` no servidor.
 *
 * O valor gravado na regra é uma SERIALIZAÇÃO de tokens (ver `valueTokens.ts`), não o
 * texto da opção. Mandá-lo cru vaza o separador para dentro da query e não casa nada —
 * foi o que esvaziou o Kanban com uma opção que tem vírgula ("Sim, auxílio doença").
 * Multi-token é interceptado antes (`applyMultiEqualityOnDealColumn`); aqui só o caso
 * de um token. Valor com múltiplos tokens que chegue aqui é devolvido cru de propósito,
 * para não mudar em silêncio o recorte de um filtro antigo.
 */
function equalityValue(raw: string): string {
  const parts = splitFilterValue(raw);
  return parts.length === 1 ? parts[0] : raw;
}

function applyCondition(q: any, field: string, operator: string, value: string) {
  switch (operator) {
    case "equals": return q.eq(field, equalityValue(value));
    case "not_equals": return q.neq(field, equalityValue(value));
    case "contains": return q.ilike(field, `%${value}%`);
    case "not_contains": return q.not(field, "ilike", `%${value}%`);
    case "greater_than": return q.gt(field, value);
    case "less_than": return q.lt(field, value);
    case "is_empty": return q.is(field, null);
    case "is_not_empty": return q.not(field, "is", null);
    case "after": return q.gt(field, value);
    case "before": return q.lt(field, value);
    case "gt": return q.gt(field, value);
    case "lt": return q.lt(field, value);
    case "gte": return q.gte(field, value);
    case "lte": return q.lte(field, value);
    default: return null;
  }
}

export interface ApplyDealSavedFilterResult {
  query: any;
  hasRemainingClientFilters: boolean;
}

export async function fetchDealCustomFieldTypesMap(orgId: string): Promise<Record<string, string>> {
  const { data } = await supabase
    .from("organization_custom_fields")
    .select("field_name, field_type")
    .eq("org_id", orgId)
    .eq("page_type", "deals");
  const map: Record<string, string> = {};
  (data || []).forEach((f: any) => {
    if (f?.field_name) map[f.field_name] = f.field_type;
  });
  return map;
}

/**
 * QueryKey do mapa field_name→field_type dos custom fields de `deals`.
 * ⚠️ Ancorado no prefixo `['organization-custom-fields', …]` de propósito: as
 * mutations de custom field (useOrganizationCustomFields / useFieldRegistry) já
 * chamam `invalidateQueries({ queryKey: ['organization-custom-fields'] })`, que é
 * prefix-match — logo este mapa derivado é invalidado junto, sem precisar tocar
 * cada mutation. Segmento final distinto de qualquer `page_type` (deals/leads/…)
 * usado por `useOrganizationCustomFieldsByPage`, então não colide.
 */
export const DEALS_CF_TYPES_QK = (orgId: string | null) =>
  ["organization-custom-fields", orgId, "__deals_types_map"] as const;

/**
 * Versão cacheada de {@link fetchDealCustomFieldTypesMap}. Config `structural`
 * (30 min, sem refetch cego) via `queryClient.fetchQuery`: coalesce todas as
 * leituras (kanban + lista + `fetchAllFilteredDealIds`) num único fetch por org
 * a cada 30 min. Antes, cada query de deals refazia esse SELECT sem cache —
 * ~13/min em prod (pg_stat_statements 03/07). Metadados de custom field mudam
 * raramente e a invalidação por mutation mantém o mapa fresco (ver QK acima).
 */
export function fetchDealCustomFieldTypesMapCached(
  orgId: string,
): Promise<Record<string, string>> {
  return queryClient.fetchQuery({
    queryKey: DEALS_CF_TYPES_QK(orgId),
    queryFn: () => fetchDealCustomFieldTypesMap(orgId),
    staleTime: queryConfigs.structural.staleTime,
  });
}

/**
 * Aplica regras do filtro salvo + exclusão padrão de status.
 * Não aplica `pipeline_id`, `stage_id` nem `owner` — o caller encadeia depois.
 *
 * @param excludeWonLost  Quando `true` (padrão, Kanban), exclui won/lost automaticamente
 *                        se o filtro não tiver regra de status.
 *                        Quando `false` (list view), só exclui `deleted`.
 */
export function applyDealSavedFilterToQuery(
  query: any,
  activeFilter: { grupos?: any[] } | null,
  cfTypes: Record<string, string>,
  excludeWonLost = true
): ApplyDealSavedFilterResult {
  let q = query;
  let hasRemainingClientFilters = false;
  let statusAppliedServerSide = false;

  const filterTargetsStatus = filterHasFieldRule(activeFilter, "status");

  if (activeFilter?.grupos?.length) {
    for (const grupo of activeFilter.grupos) {
      const regras = grupo.regras || grupo.filtro_regras || [];
      if (regras.length === 0) continue;

      if (grupo.tipo_agrupamento === "AND") {
        for (const regra of regras) {
          // Cadence virtual fields are pre-resolved before this function runs
          // (see resolveCadenceRules in cadenceFilterResolver.ts). Skip here.
          const normalizedCampo = normalizeFilterFieldPath(String(regra.campo_filtrado || "").trim());
          if (isCadenceFilterField(normalizedCampo)) continue;

          const campo = (regra.campo_filtrado || "").replace(/^deals\./, "");

          if (campo.startsWith("custom_data.")) {
            const cfName = campo.slice("custom_data.".length);
            const cfType = cfTypes[cfName];
            const jsonbField = `custom_data->>${cfName}`;

            if (cfType === "date") {
              const valRaw = String(regra.valor ?? "").trim();
              if (
                (regra.operador === "equals" || regra.operador === "not_equals") &&
                RELATIVE_DATE_OPS.has(valRaw)
              ) {
                const range = relativeDateToRange(valRaw);
                if (range) {
                  const fromDate = range.from.slice(0, 10);
                  const toDate = range.to.slice(0, 10);
                  if (regra.operador === "equals") {
                    q = q.gte(jsonbField, fromDate).lte(jsonbField, toDate);
                  } else {
                    q = q.or(`${jsonbField}.lt.${fromDate},${jsonbField}.gt.${toDate}`);
                  }
                  continue;
                }
              }
              if (RELATIVE_DATE_OPS.has(regra.operador)) {
                const range = relativeDateToRange(regra.operador);
                if (range) {
                  const fromDate = range.from.slice(0, 10);
                  const toDate = range.to.slice(0, 10);
                  q = q.gte(jsonbField, fromDate).lte(jsonbField, toDate);
                  continue;
                }
              }
              if (["before", "after", "equals", "between"].includes(regra.operador) &&
                  regra.valor != null && regra.valor !== "") {
                if (regra.operador === "between") {
                  const parts = String(regra.valor).split("|");
                  const fromRaw = parts[0]?.trim();
                  const toRaw = parts[1]?.trim();
                  if (fromRaw) q = q.gte(jsonbField, fromRaw);
                  if (toRaw) q = q.lte(jsonbField, toRaw);
                  continue;
                }
                const refRaw = String(regra.valor).trim();
                if (regra.operador === "equals") {
                  q = q.eq(jsonbField, refRaw);
                  continue;
                }
                if (regra.operador === "after") {
                  q = q.gt(jsonbField, refRaw);
                  continue;
                }
                if (regra.operador === "before") {
                  q = q.lt(jsonbField, refRaw);
                  continue;
                }
              }
              if (regra.operador === "is_empty") {
                q = q.or(`custom_data->>${cfName}.is.null,custom_data->>${cfName}.eq.`);
                continue;
              }
              if (regra.operador === "is_not_empty") {
                q = q.not(jsonbField, "is", null).neq(jsonbField, "");
                continue;
              }
            }

            if (cfType === "text" || cfType === "textarea" || cfType === "select" || cfType === "user") {
              // O gate era `raw.includes(",")`: uma opção com vírgula ("Sim, auxílio
              // doença") entrava no ramo multi, resolvia para UM token e caía no `eq`
              // mandando o valor SERIALIZADO (com o separador) — zero linhas.
              if (regra.operador === "equals" && regra.valor != null) {
                const pts = parseCsvTokens(String(regra.valor));
                if (pts.length > 1) {
                  q = q.in(jsonbField, pts);
                  continue;
                }
                q = q.eq(jsonbField, pts[0] ?? "");
                continue;
              }
              if (regra.operador === "not_equals" && regra.valor != null) {
                const pts = parseCsvTokens(String(regra.valor));
                if (pts.length > 1) {
                  q = q.not(jsonbField, "in", `(${postgrestInList(pts)})`);
                  continue;
                }
                q = q.neq(jsonbField, pts[0] ?? "");
                continue;
              }
              if (regra.operador === "contains" && regra.valor != null && regra.valor !== "") {
                q = q.ilike(jsonbField, `%${regra.valor}%`);
                continue;
              }
              if (regra.operador === "not_contains" && regra.valor != null && regra.valor !== "") {
                // Explicitly exclude NULLs: PostgREST's not.ilike includes NULL rows
                q = q.not(jsonbField, "is", null)
                     .not(jsonbField, "ilike", `%${regra.valor}%`);
                continue;
              }
              if (regra.operador === "is_empty") {
                q = q.or(`custom_data->>${cfName}.is.null,custom_data->>${cfName}.eq.`);
                continue;
              }
              if (regra.operador === "is_not_empty") {
                q = q.not(jsonbField, "is", null).neq(jsonbField, "");
                continue;
              }
            }

            hasRemainingClientFilters = true;
            continue;
          }

          if (DEAL_COLS.has(campo) && DATE_COLS.has(campo) && RELATIVE_DATE_OPS.has(regra.operador)) {
            const range = relativeDateToRange(regra.operador);
            if (range) {
              const r1 = applyCondition(q, campo, "gte", range.from);
              if (r1) q = r1;
              const r2 = applyCondition(q, campo, "lte", range.to);
              if (r2) q = r2;
              continue;
            }
          }

          if (DEAL_COLS.has(campo) && DATE_COLS.has(campo)) {
            const valRel = String(regra.valor ?? "").trim();
            if (
              (regra.operador === "equals" || regra.operador === "not_equals") &&
              RELATIVE_DATE_OPS.has(valRel)
            ) {
              const range = relativeDateToRange(valRel);
              if (range) {
                if (regra.operador === "equals") {
                  const r1 = applyCondition(q, campo, "gte", range.from);
                  if (r1) q = r1;
                  const r2 = applyCondition(q, campo, "lte", range.to);
                  if (r2) q = r2;
                } else {
                  q = q.or(`${campo}.lt.${range.from},${campo}.gt.${range.to}`);
                }
                continue;
              }
            }
          }

          if (DEAL_COLS.has(campo) && DATE_COLS.has(campo) &&
              regra.valor != null && regra.valor !== "" &&
              ["before", "after", "equals", "between"].includes(regra.operador)) {
            if (regra.operador === "between") {
              const parts = String(regra.valor).split("|");
              const fromD = parts[0]?.trim() ? parseISO(parts[0].trim()) : null;
              const toD = parts[1]?.trim() ? parseISO(parts[1].trim()) : null;
              if (fromD && isValid(fromD)) {
                const r = applyCondition(q, campo, "gte", startOfDay(fromD).toISOString());
                if (r) q = r;
              }
              if (toD && isValid(toD)) {
                const r = applyCondition(q, campo, "lte", endOfDay(toD).toISOString());
                if (r) q = r;
              }
              continue;
            }
            const ref = parseISO(String(regra.valor));
            if (isValid(ref)) {
              if (regra.operador === "equals") {
                const r1 = applyCondition(q, campo, "gte", startOfDay(ref).toISOString());
                if (r1) q = r1;
                const r2 = applyCondition(q, campo, "lte", endOfDay(ref).toISOString());
                if (r2) q = r2;
                continue;
              }
              if (regra.operador === "after") {
                const r = applyCondition(q, campo, "gt", endOfDay(ref).toISOString());
                if (r) q = r;
                continue;
              }
              if (regra.operador === "before") {
                const r = applyCondition(q, campo, "lt", startOfDay(ref).toISOString());
                if (r) q = r;
                continue;
              }
            }
          }

          if (DEAL_COLS.has(campo) && SERVER_OPS[regra.operador]) {
            const requiresNumericValue = NUMERIC_COLS.has(campo) &&
              !["is_empty", "is_not_empty"].includes(regra.operador);
            if (requiresNumericValue && regra.valor != null && regra.valor !== "" &&
                isNaN(Number(regra.valor))) {
              hasRemainingClientFilters = true;
              continue;
            }

            // `isMultiToken` e não `includes(",")`: uma opção única com vírgula não é
            // multi-valor. Com o gate antigo ela caía no `hasRemainingClientFilters`
            // abaixo e virava filtro client-side sobre a PÁGINA — no Kanban, 20 cards
            // por etapa, o que esvazia as colunas.
            if (
              (regra.operador === "equals" || regra.operador === "not_equals") &&
              regra.valor != null &&
              isMultiToken(String(regra.valor))
            ) {
              const multi = applyMultiEqualityOnDealColumn(
                q,
                campo,
                regra.operador,
                String(regra.valor)
              );
              if (multi) {
                q = multi;
                if (campo === "status") statusAppliedServerSide = true;
                continue;
              }
              hasRemainingClientFilters = true;
              continue;
            }

            const result = applyCondition(q, campo, regra.operador, String(regra.valor ?? ""));
            if (result) {
              q = result;
              if (campo === "status") statusAppliedServerSide = true;
            } else {
              hasRemainingClientFilters = true;
            }
          } else {
            hasRemainingClientFilters = true;
          }
        }
      } else {
        hasRemainingClientFilters = true;
      }
    }
  }

  if (!filterTargetsStatus && !statusAppliedServerSide) {
    if (excludeWonLost) {
      // Kanban: esconde won/lost por padrão
      q = q.not("status", "in", '("won","lost","deleted")');
    } else {
      // List view: mostra won/lost, só esconde deleted
      q = q.not("status", "eq", "deleted");
    }
  } else if (!statusAppliedServerSide) {
    q = q.not("status", "eq", "deleted");
  }
  if (statusAppliedServerSide) {
    q = q.not("status", "eq", "deleted");
  }

  return { query: q, hasRemainingClientFilters };
}
